import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsIcon from '@mui/icons-material/Collections';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import StripedDataGrid from 'shared/components/StripedDataGrid';
import AuctionIntelligenceService from 'services/AuctionIntelligenceService';
import { CompetitorsResponse, MoverStats, TransparencyResponse } from '../types';
import {
  analysisPeriod,
  attackExposureForDomain,
  auctionInsightsUrl,
  classifyExpansion,
  entityDeltas,
  expansionShareForDomain,
  exposureByDomain,
  matchesCategories,
  priorSpendMap,
  spendMap,
  weightedDeltaPp,
  ATTACK_EXPOSURE_FLOOR,
  FILTER_OPTIONS,
  FilterCategory,
  MoverClassification,
} from '../analysis';


// Account-wide competitor movers, ranked by $-EXPOSURE (share gain × our
// weekly spend on the attacked ad groups) — a +55pp grab on a $10/wk ad group
// ranks below +5pp on a $5k/wk one. The PillowHome backtest showed slow
// grinders only surface per-ad-group, so the drill-down (zoom icon) shows
// exactly which ad groups a domain is gaining in, with Google Ads deep links.

const pct = (v: number | null | undefined): string =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;

const money = (v: number | null | undefined): string =>
  v == null ? '—' : `$${Math.round(v).toLocaleString()}`;

interface Props {
  service: AuctionIntelligenceService;
}

type MoverRow = MoverStats & {
  id: string;
  exposure: number;
  attackExposure: number;
  classification: MoverClassification;
};

export default function MoversTab({ service }: Props) {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState<CompetitorsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [hideLowCoverage, setHideLowCoverage] = useState(true);
  const [typeFilter, setTypeFilter] = useState<FilterCategory[]>([]);
  const [drilldown, setDrilldown] = useState<string | null>(null);
  const [creativesFor, setCreativesFor] = useState<string | null>(null);
  const [creatives, setCreatives] = useState<TransparencyResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    service
      .getCompetitors({ weeks })
      .then(resp => {
        if (!cancelled) setData(resp);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [service, weeks]);

  const spend = useMemo(() => spendMap(data?.entitySpend), [data]);
  const priorSpend = useMemo(() => priorSpendMap(data?.entitySpend), [data]);
  const period = useMemo(() => (data ? analysisPeriod(data.rows) : null), [data]);
  const you = data?.movers.find(m => m.isYou);

  const rows = useMemo<MoverRow[]>(() => {
    if (!data) return [];
    const competitors = data.movers.filter(m => !m.isYou);
    const exposure = exposureByDomain(data.rows, competitors, spend);
    const gated = hideLowCoverage ? competitors.filter(m => m.entities >= 2) : competitors;
    const enriched = gated.map((m, i) => {
      const deltas = entityDeltas(data.rows, m.domain, spend, priorSpend);
      return {
        id: `${m.domain}-${i}`,
        ...m,
        // Digest parity: the alert's headline Δ is spend-weighted across ad
        // groups, not the account-wide share average the server returns.
        deltaPp: weightedDeltaPp(deltas) ?? m.deltaPp,
        exposure: exposure.get(m.domain) ?? 0,
        attackExposure: attackExposureForDomain(deltas),
        classification: classifyExpansion(expansionShareForDomain(deltas)),
      };
    });
    return enriched.filter(m => matchesCategories(m, typeFilter));
  }, [data, hideLowCoverage, spend, priorSpend, typeFilter]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'domain',
        headerName: 'Domain',
        flex: 1,
        minWidth: 220,
        renderCell: (p: GridRenderCellParams<unknown, MoverRow>) => (
          <span>
            {p.row.domain}{' '}
            {p.row.isNewEntrant && <Chip label="new entrant" color="error" size="small" />}
            {p.row.isReturning && <Chip label="returned" color="warning" size="small" />}
            {p.row.classification === 'our_expansion' && (
              <Chip
                label="our expansion"
                color="success"
                size="small"
                title="Their share gain sits on ad groups WE just launched or scaled — expected overlap, not an attack"
              />
            )}
            {p.row.classification === 'mixed' && (
              <Chip
                label="partly our expansion"
                size="small"
                title="Some of this gain sits on ad groups we recently scaled"
              />
            )}
            {p.row.classification !== 'competitor_gain' &&
              p.row.attackExposure >= ATTACK_EXPOSURE_FLOOR && (
                <Chip
                  label={`⚠ attack $${p.row.attackExposure}/wk`}
                  color="error"
                  size="small"
                  title="Even though this rides our expansion, this much of the exposure sits on mature flat-spend ad groups — a genuine attack that pages"
                />
              )}
          </span>
        ),
      },
      {
        field: 'exposure',
        headerName: '$ exposed/wk',
        width: 115,
        type: 'number',
        renderCell: (p: GridRenderCellParams) => {
          const v = Number(p.value ?? 0);
          return (
            <span style={{ fontWeight: v >= 500 ? 700 : 400, color: v >= 2500 ? '#c62828' : undefined }}>
              {money(v)}
            </span>
          );
        },
      },
      {
        field: 'deltaPp',
        headerName: 'Δ (spend-wtd)',
        width: 95,
        type: 'number',
        renderCell: (p: GridRenderCellParams) => {
          const v = p.value == null ? null : Number(p.value);
          if (v == null) return '—';
          return (
            <span style={{ color: v >= 5 ? '#c62828' : v <= -5 ? '#2e7d32' : undefined }}>
              {v > 0 ? '+' : ''}
              {v.toFixed(1)}pp
            </span>
          );
        },
      },
      {
        field: 'currentShare',
        headerName: 'Avg now',
        width: 105,
        type: 'number',
        valueFormatter: ({ value }) => pct(value == null ? null : Number(value)),
      },
      {
        field: 'priorShare',
        headerName: 'Avg prior',
        width: 105,
        type: 'number',
        valueFormatter: ({ value }) => pct(value == null ? null : Number(value)),
      },
      { field: 'entities', headerName: 'Ad groups', width: 90, type: 'number' },
      { field: 'weeksSeen', headerName: 'Weeks', width: 80, type: 'number' },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<unknown, MoverRow>) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              title="Which ad groups?"
              onClick={() => setDrilldown(p.row.domain)}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="View creatives (Ads Transparency Center)"
              onClick={async () => {
                setCreativesFor(p.row.domain);
                setCreatives(null);
                setCreatives(await service.getTransparency(p.row.domain));
              }}
            >
              <CollectionsIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [service],
  );

  if (loading) return <CircularProgress sx={{ m: 4 }} />;

  if (!data || data.rows.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No competitor data yet. Upload an auction-insights CSV export (or wait
        for the weekly report email) to populate this view.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        {you && (
          <Typography variant="body2" color="text.secondary">
            Average observed ad-group share: <b>{pct(you.currentShare)}</b> (last 2 complete wks) vs{' '}
            {pct(you.priorShare)} (prior 4)
            {you.deltaPp != null && (
              <b style={{ color: you.deltaPp < 0 ? '#c62828' : '#2e7d32' }}>
                {' '}
                {you.deltaPp > 0 ? '+' : ''}
                {you.deltaPp}pp
              </b>
            )}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ width: 220 }}>
          <InputLabel id="type-filter-label" shrink>
            Type
          </InputLabel>
          <Select
            multiple
            displayEmpty
            labelId="type-filter-label"
            input={<OutlinedInput notched label="Type" />}
            value={typeFilter}
            onChange={e =>
              setTypeFilter(
                typeof e.target.value === 'string'
                  ? (e.target.value.split(',') as FilterCategory[])
                  : (e.target.value as FilterCategory[]),
              )
            }
            renderValue={selected =>
              selected.length === 0
                ? 'All movers'
                : FILTER_OPTIONS.filter(o => selected.includes(o.value))
                    .map(o => o.label)
                    .join(', ')
            }
          >
            {FILTER_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox size="small" checked={typeFilter.includes(o.value)} />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          select
          size="small"
          label="History"
          value={weeks}
          onChange={e => setWeeks(Number(e.target.value))}
          sx={{ width: 130 }}
        >
          <MenuItem value={8}>8 weeks</MenuItem>
          <MenuItem value={12}>12 weeks</MenuItem>
          <MenuItem value={25}>25 weeks</MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideLowCoverage}
              onChange={e => setHideLowCoverage(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Hide one-off domains</Typography>}
        />
      </Stack>

      <Box sx={{ height: 600 }}>
        <StripedDataGrid
          rows={rows}
          columns={columns}
          density="compact"
          disableSelectionOnClick
          getRowClassName={params =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          initialState={{ sorting: { sortModel: [{ field: 'exposure', sort: 'desc' }] } }}
        />
      </Box>

      <Dialog open={drilldown != null} onClose={() => setDrilldown(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {drilldown} — where they&apos;re showing (ranked by Δ × our spend)
          <IconButton onClick={() => setDrilldown(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {drilldown && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Campaign › Ad group</TableCell>
                  <TableCell align="right">Our spend/wk</TableCell>
                  <TableCell align="right">Now</TableCell>
                  <TableCell align="right">Prior</TableCell>
                  <TableCell align="right">Δpp</TableCell>
                  <TableCell>Last 6 wks</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {entityDeltas(data.rows, drilldown, spend, priorSpend).map(row => (
                  <TableRow key={row.entityLabel} hover>
                    <TableCell sx={{ maxWidth: 340 }}>
                      {row.entityLabel}
                      {row.ourSpendRamped && (
                        <Chip
                          label="we ramped"
                          color="success"
                          size="small"
                          sx={{ ml: 0.5 }}
                          title="Our spend on this ad group jumped from ~nothing — their share here reflects our entry into these auctions"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">{money(row.weeklySpend)}</TableCell>
                    <TableCell align="right">{pct(row.currentShare)}</TableCell>
                    <TableCell align="right">{pct(row.priorShare)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: (row.deltaPp ?? 0) > 0 ? 'error.main' : 'success.main' }}
                    >
                      {row.deltaPp == null ? '—' : `${row.deltaPp > 0 ? '+' : ''}${row.deltaPp}`}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.history}</TableCell>
                    <TableCell>
                      {row.campaignId && (
                        <Link
                          href={auctionInsightsUrl(data.accountId, row.campaignId, {
                            adGroupId: row.adGroupId,
                            ...(period ?? {}),
                          })}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ fontSize: 12 }}
                        >
                          Auction insights
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={creativesFor != null} onClose={() => setCreativesFor(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Creatives — {creativesFor}
          <IconButton onClick={() => setCreativesFor(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {creatives == null ? (
            <CircularProgress size={24} />
          ) : !creatives.configured ? (
            <Alert severity="warning">
              Automated lookup unavailable ({creatives.reason}).{' '}
              <Link
                href={`https://adstransparency.google.com/?domain=${encodeURIComponent(creativesFor ?? '')}`}
                target="_blank"
                rel="noreferrer"
              >
                Open {creativesFor} in the Google Ads Transparency Center
              </Link>
            </Alert>
          ) : creatives.creatives.length === 0 ? (
            <Typography variant="body2">No running creatives found.</Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {creatives.creatives.slice(0, 24).map((c, i) => (
                <Box key={i} sx={{ width: 220 }}>
                  {c.preview ? (
                    <img src={c.preview} alt={`${creativesFor} creative`} style={{ maxWidth: '100%', borderRadius: 4 }} />
                  ) : (
                    <Chip label={c.format} size="small" />
                  )}
                  <Typography variant="caption" display="block">
                    {c.format}
                    {c.lastShown ? ` · last shown ${c.lastShown}` : ''}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
